<template>
  <div class="users-page">
    <block-buttons
      title="Сотрудники"
      description="Управляйте сотрудниками"
      :actions="toolbarActions"
    >
      <template #icon>
        <Users :size="22" />
      </template>
    </block-buttons>

    <data-table
      :rows="renderData"
      :columns="tableColumns"
      :loading="loading"
      :error="error"
      title="Сотрудники"
      description="Управление сотрудниками"
      search-placeholder="Поиск по сотрудникам"
      empty-text="Сотрудников пока нет"
      @retry="loadDataWrapper(true)"
    >
      <template #header>
        <span class="users-page__count">Сотрудников: {{ users.length }}</span>
      </template>

      <template #cell-title="{ row }">
        <strong>{{ row.title }}</strong>
      </template>

      <template #cell-statusText="{ row }">
        <span
          class="user__status"
          :class="`user__${row.status}`"
        >
          {{ row.statusText }}
        </span>
      </template>

      <template #cell-actions="{ row }">
        <select
          :disabled="loading"
          :aria-label="`Действия с сотрудником ${row.title}`"
          @change="selectAction($event, row)"
        >
          <option value="">Действия</option>

          <option
            v-for="action in rowActions(row)"
            :key="action.id"
            :value="action.id"
          >
            {{ action.label }}
          </option>
        </select>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import {
  Users,
  Plus,
  RefreshCw
} from 'lucide-vue'

import BlockButtons from '@/components/blockButtons'
import DataTable from '@/components/dataTable'
import permissions from '@/mixins/permissions'

export default {
  name: 'OnlineOrderV2',
  components: {
    Users,
    BlockButtons,
    DataTable
  },
  mixins: [permissions],
  data () {
    return {
      loading: false,
      error: ''
    }
  },
  computed: {
    ...mapGetters('users', { users: 'getData' }),
    ...mapGetters('settings', ['getTableColumns', 'getDefaultValues']),
    renderData () {
      const statusMap = {
        'activated': 'Активен',
        'deactivated': 'Деактивирован'
      }

      return this.users.map(user => ({
        ...user,
        role: this.getDefaultValues?.role?.find(({ code }) => code === user.role)?.name,
        statusText: statusMap[user.status]
      }))
    },
    toolbarActions () {
      const actions = [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: () => this.loadDataWrapper(true)
        }
      ]

      if (this.isAllowedOperation('create', 'users')) {
        actions.push({
          id: 'create',
          label: 'Добавить нового сотрудника',
          icon: Plus,
          primary: true,
          disabled: this.loading,
          handler: () => {
            this.openModal({
              name: 'create-base',
              propsData: {
                name: 'users',
                moduleAction: 'users',
                type: 'create',
                title: 'Добавление нового сотрудника',
                text: 'Укажите данные сотрудника',
                textCancelBtn: 'Закрыть',
                textConfirmBtn: 'Добавить сотрудника'
              }
            })
          }
        })
      }

      return actions
    },
    tableColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['users'] || [])
      ]
    }
  },
  mounted () {
    this.loadDataWrapper()
  },
  methods: {
    ...mapActions('users', ['loadData', 'remove', 'deactivate', 'activate']),
    ...mapActions('modal', ['openModal']),
    rowActions (row) {
      return [
        {
          id: 'info',
          label: 'Информация',
          visible: true
        },
        {
          id: 'change',
          label: 'Редактировать',
          visible: this.isAllowedOperation('change', 'user')
        },
        {
          id: 'deactivate',
          label: 'Деактивировать',
          visible: this.isAllowedOperation('deactivateUser', 'user') && row.status === 'activated'
        },
        {
          id: 'activate',
          label: 'Активировать',
          visible: this.isAllowedOperation('activateUser', 'user') && row.status === 'deactivated'
        },
        {
          id: 'remove',
          label: 'Удалить',
          visible: this.isAllowedOperation('remove', 'user')
        }
      ].filter(({ visible }) => visible)
    },
    async loadDataWrapper (force = false) {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      await this.loadData({ force })

      this.loading = false
      this.$bus.emit('stop-loader')
    },
    selectAction (event, row) {
      const action = event.target.value

      event.target.value = ''

      if (!action) {
        return
      }

      this.openAction(action, row)
    },
    openAction(action, row) {
      switch (action) {
        case 'info':
          this.openModal({
            name: 'info-base',
            propsData: {
              name: 'users',
              moduleAction: 'users',
              type: 'info',
              title: `Информация - ${row.name} ${row.surname}`,
              textCancelBtn: 'Закрыть',
              data: row
            }
          })

          break
        case 'change':
          this.openModal({
            name: 'change-base',
            propsData: {
              name: 'users',
              moduleAction: 'users',
              type: 'change',
              title: `Редактирование - ${row.name} ${row.surname}`,
              text: 'Измените необходимые данные',
              textCancelBtn: 'Закрыть',
              textConfirmBtn: 'Сохранить',
              data: row
            }
          })

          break
        case 'deactivate':
          this.deactivateUser(row)

          break

        case 'activate':
          this.activateUser(row)

          break
        case 'remove':
          this.removeUser(row)
      }
    },
    async removeUser ({ id }) {
      const { error } = await this.remove(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при удалении!',
          text: 'Произошла ошибка при удалении сотрудника!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.$notify({
        type: 'success',
        title: 'Сотрудник успешно удалена!',
        text: '',
        clean: false,
        duration: 3000
      })
    },
    async activateUser ({ id }) {
      const { error } = await this.activate(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при активации!',
          text: 'Произошла ошибка при активации сотрудника!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.$notify({
        type: 'success',
        title: 'Сотрудник успешно активирован!',
        text: '',
        clean: false,
        duration: 3000
      })
    },
    async deactivateUser ({ id }) {
      const { error } = await this.deactivate(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при деактивации!',
          text: 'Произошла ошибка при деактивации сотрудника!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.$notify({
        type: 'success',
        title: 'Сотрудник успешно деактивирован!',
        text: '',
        clean: false,
        duration: 3000
      })
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
